/**
 * Fish Button
 * Main interaction button to start fishing, with circular progress ring
 * and sweet spot timing mechanic for bonus catch chance.
 *
 * Timing tiers:
 *   Perfect — click lands inside the sweet spot zone → +15% catch chance
 *   Good    — click lands in the margin zone around the sweet spot → +7% catch chance
 *   Miss    — click lands elsewhere during fishing → no bonus, no penalty
 *
 * One click allowed per cast. The button is always interactive while fishing.
 */

import { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { FishingState, GameEvent, EquipmentSlot } from '@/types';
import { useFishingState, usePlayerStats, useCurrentZone, useEquippedGear, useGameStore } from '@/state';
import { fishingEngine, saveManager, mentorManager } from '@/systems';
import { eventBus } from '@/services/EventBus';
import { getZoneDefinition, BALANCE_CONFIG, EQUIPMENT_DATA } from '@/data';
import { calculateFishingTime } from '@/utils';
import { FlyCastOverlay } from './FlyCastOverlay';

const RING_RADIUS = 100;
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS;

/** Base sweet spot size at reference fishing time (15% of ring) */
const BASE_SWEET_SPOT_SIZE = 0.15;
/** Base margin on each side of the perfect zone for "Good" hits */
const BASE_GOOD_ZONE_MARGIN = 0.11;
/** Max sweet spot size cap (40% of ring) to prevent trivially easy timing */
const MAX_SWEET_SPOT_SIZE = 0.40;
/** Max total good zone cap (sweet spot + margins, 60% of ring) */
const MAX_GOOD_ZONE_TOTAL = 0.60;
/** Catch chance bonus for hitting the sweet spot perfectly */
const PERFECT_BONUS = 0.15;
/** Catch chance bonus for a "Good" hit near the sweet spot */
const GOOD_BONUS = 0.07;
/** No bonus for a miss */
const MISS_BONUS = 0;

type ClickResult = 'perfect' | 'good' | 'miss' | null;

export function FishButton() {
  const fishingState = useFishingState();
  const inventory = useGameStore((state) => state.playerState.inventory);
  const playerStats = usePlayerStats();
  const currentZoneId = useCurrentZone();
  const equippedGear = useEquippedGear();
  const hasSeenTimingTutorial = useGameStore((state) => state.playerState.settings.hasSeenTimingTutorial);
  const [showTooltip, setShowTooltip] = useState(false);
  const [clickResult, setClickResult] = useState<ClickResult>(null);
  const [showTimingTutorial, setShowTimingTutorial] = useState(false);
  const [showFlyCast, setShowFlyCast] = useState(false);

  // Detect if equipped rod is a fly rod
  const isFlyRod = useMemo(() => {
    const rodId = equippedGear[EquipmentSlot.ROD];
    if (!rodId) return false;
    const rodDef = EQUIPMENT_DATA[rodId];
    return rodDef?.isFlyRod === true;
  }, [equippedGear]);

  // Scale sweet spot size inversely with fishing speed so the real-time
  // window stays roughly constant regardless of cast duration.
  const { sweetSpotSize, goodZoneMargin } = useMemo(() => {
    const zone = getZoneDefinition(currentZoneId);
    const actualFishingTime = calculateFishingTime(zone.baseFishingTime, playerStats);
    const ratio = BALANCE_CONFIG.BASE_FISHING_TIME / actualFishingTime;

    const rawSweetSpot = BASE_SWEET_SPOT_SIZE * ratio;
    const cappedSweetSpot = Math.min(MAX_SWEET_SPOT_SIZE, rawSweetSpot);

    const rawGoodMargin = BASE_GOOD_ZONE_MARGIN * ratio;
    // Cap total good zone (sweetSpot + 2*margin) to MAX_GOOD_ZONE_TOTAL
    const maxMargin = (MAX_GOOD_ZONE_TOTAL - cappedSweetSpot) / 2;
    const cappedGoodMargin = Math.min(rawGoodMargin, maxMargin);

    return { sweetSpotSize: cappedSweetSpot, goodZoneMargin: cappedGoodMargin };
  }, [currentZoneId, playerStats]);

  // Sweet spot: random start position (0-1) set when fishing begins
  const sweetSpotStartRef = useRef(0);
  const sweetSpotHitRef = useRef(false);
  const wasFishingRef = useRef(false);

  const isFishing = fishingState.status !== FishingState.IDLE;
  const isWaiting = fishingState.status === FishingState.WAITING;
  const isInventoryFull = inventory.currentCount >= inventory.capacity;

  // Randomize sweet spot when fishing starts
  useEffect(() => {
    if (isWaiting && !wasFishingRef.current) {
      // Fishing just started — pick a random sweet spot position
      // Place it in the 20%-80% range so it's neither at the very start nor end
      sweetSpotStartRef.current = 0.2 + Math.random() * 0.6;
      sweetSpotHitRef.current = false;
      setClickResult(null);

      // Show timing tutorial on first-ever fishing cast
      if (!hasSeenTimingTutorial) {
        setShowTimingTutorial(true);
      }
    }
    wasFishingRef.current = isWaiting;
  }, [isWaiting, hasSeenTimingTutorial]);

  const dismissTimingTutorial = useCallback(() => {
    setShowTimingTutorial(false);
    useGameStore.setState((draft) => {
      draft.playerState.settings.hasSeenTimingTutorial = true;
    });
    saveManager.save();
  }, []);

  // Circular progress
  let progress = 0;
  let totalDuration = 0;
  if (isWaiting) {
    const timeRemaining = Math.max(0, fishingState.timeRemaining);
    const startedAt = fishingState.startedAt || Date.now();
    totalDuration = (Date.now() - startedAt) / 1000 + timeRemaining;
    progress = totalDuration > 0 ? (totalDuration - timeRemaining) / totalDuration : 0;
  }
  const dashoffset = RING_CIRCUMFERENCE * (1 - progress);

  // Expose debug state for dev HUD + Playwright tests
  if (import.meta.env.DEV) {
    window.__fishingDebug = {
      isFishing: isWaiting,
      progress,
      totalDuration,
      sweetSpotStart: sweetSpotStartRef.current,
      sweetSpotEnd: sweetSpotStartRef.current + sweetSpotSize,
      sweetSpotCenter: sweetSpotStartRef.current + sweetSpotSize / 2,
      goodZoneStart: sweetSpotStartRef.current - goodZoneMargin,
      goodZoneEnd: sweetSpotStartRef.current + sweetSpotSize + goodZoneMargin,
      sweetSpotHit: sweetSpotHitRef.current,
      lastClickResult: clickResult,
      constants: { SWEET_SPOT_SIZE: sweetSpotSize, GOOD_ZONE_MARGIN: goodZoneMargin, PERFECT_BONUS, GOOD_BONUS },
    };
  }

  // Sweet spot arc geometry
  const sweetSpotStart = sweetSpotStartRef.current;
  const sweetSpotCenter = sweetSpotStart + sweetSpotSize / 2;

  // Sweet spot SVG arc: dash offset/length for a partial arc
  // dashoffset = C*(1-start) places the arc at `start` fraction around the ring (clockwise from top)
  const sweetSpotDashLength = RING_CIRCUMFERENCE * sweetSpotSize;
  const sweetSpotDashOffset = RING_CIRCUMFERENCE * (1 - sweetSpotStart);

  // Good zone SVG arc (wider zone around the sweet spot)
  const goodZoneSize = sweetSpotSize + goodZoneMargin * 2;
  const goodZoneStart = sweetSpotStart - goodZoneMargin;
  const goodZoneDashLength = RING_CIRCUMFERENCE * goodZoneSize;
  const goodZoneDashOffset = RING_CIRCUMFERENCE * (1 - goodZoneStart);

  /**
   * Determine click tier based on how close progress is to the sweet spot.
   * Handles wrap-around at the 0/1 boundary.
   */
  const getClickTier = useCallback((currentProgress: number): ClickResult => {
    // Distance from progress to sweet spot center (handles wrap-around)
    const diff = Math.abs(currentProgress - sweetSpotCenter);
    const distance = Math.min(diff, 1 - diff);

    const halfPerfect = sweetSpotSize / 2;
    const halfGood = halfPerfect + goodZoneMargin;

    if (distance <= halfPerfect) {
      return 'perfect';
    }
    if (distance <= halfGood) {
      return 'good';
    }
    return 'miss';
  }, [sweetSpotCenter, sweetSpotSize, goodZoneMargin]);

  // Fly cast overlay completed — fish was already caught (or not) during the overlay.
  // Just close the overlay and return to idle.
  const handleFlyCastComplete = useCallback(() => {
    setShowFlyCast(false);
  }, []);

  const handleClick = useCallback(() => {
    // Record player activity for mentor bonus (active play buffs buddies)
    mentorManager.recordPlayerActivity();

    // During fishing with conventional rod: measure timing (one click per cast)
    if (isWaiting && !isFlyRod && !sweetSpotHitRef.current) {
      sweetSpotHitRef.current = true;

      const tier = getClickTier(progress);
      const bonus = tier === 'perfect' ? PERFECT_BONUS
        : tier === 'good' ? GOOD_BONUS
        : MISS_BONUS;

      if (bonus > 0) {
        fishingEngine.applySweetSpotBonus(bonus);
      }

      // Perfect timing reward: cut remaining fishing time in half
      if (tier === 'perfect') {
        fishingEngine.reduceRemainingTime(0.5);
      }

      // Emit sweet spot event for quest tracking
      eventBus.emit(GameEvent.SWEET_SPOT_HIT, { tier: tier! });

      setClickResult(tier);
      const flashDuration = tier === 'miss' ? 400 : 600;
      setTimeout(() => setClickResult(null), flashDuration);
      return; // Don't start a new cast
    }

    // IDLE + not disabled: start new cast
    if (!isFishing && !isInventoryFull) {
      if (isFlyRod) {
        // Fly rod: open fly cast minigame FIRST, fishing starts after completion
        setShowFlyCast(true);
      } else {
        // Conventional rod: start fishing timer immediately
        fishingEngine.startFishing();
      }
    }
  }, [isFishing, isWaiting, isInventoryFull, isFlyRod, progress, getClickTier]);

  // Determine button appearance based on state
  const getButtonClass = () => {
    if (isInventoryFull) {
      return 'bg-red-600 cursor-not-allowed opacity-50';
    }
    if (isFishing) {
      if (sweetSpotHitRef.current) {
        // Already clicked — show muted state
        return 'bg-gray-700 cursor-default opacity-40';
      }
      // Waiting for player to click — darker blue, always interactive
      return 'bg-blue-800/70 hover:bg-blue-700/80 cursor-pointer opacity-80';
    }
    return 'bg-blue-500 hover:bg-blue-600 active:scale-95 shadow-lg hover:shadow-xl';
  };

  const tooltipText = isInventoryFull
    ? 'Inventory is full! Sell some fish first.'
    : isFishing
    ? sweetSpotHitRef.current
      ? isFlyRod ? 'Fly cast used!' : 'Timing used!'
      : isFlyRod ? 'Tap to cast your fly!' : 'Tap to time your catch!'
    : isFlyRod
    ? 'Click to cast! Trace the line for a perfect fly cast.'
    : 'Click to cast your line! Time your catch in the golden zone for bonus catch chance.';

  const handleTouchStart = () => {
    if (isInventoryFull) {
      setShowTooltip(true);
      setTimeout(() => setShowTooltip(false), 2000);
    }
  };

  return (
    <div
      className="relative inline-block"
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
      onTouchStart={handleTouchStart}
    >
      {/* Circular progress ring */}
      <svg
        className="absolute -inset-2 pointer-events-none"
        viewBox="0 0 208 208"
        fill="none"
      >
        {/* Background track (visible when fishing) */}
        {isWaiting && (
          <circle
            cx="104"
            cy="104"
            r={RING_RADIUS}
            stroke="rgba(55, 65, 81, 0.5)"
            strokeWidth="5"
          />
        )}

        {/* Good zone arc — subtle wider zone around sweet spot */}
        {isWaiting && !sweetSpotHitRef.current && (
          <circle
            cx="104"
            cy="104"
            r={RING_RADIUS}
            stroke="rgba(147, 197, 253, 0.15)"
            strokeWidth="10"
            strokeLinecap="round"
            strokeDasharray={`${goodZoneDashLength} ${RING_CIRCUMFERENCE - goodZoneDashLength}`}
            strokeDashoffset={goodZoneDashOffset}
            transform="rotate(-90 104 104)"
          />
        )}

        {/* Sweet spot arc — bright zone on the ring */}
        {isWaiting && (
          <circle
            cx="104"
            cy="104"
            r={RING_RADIUS}
            stroke={sweetSpotHitRef.current ? 'rgba(74, 222, 128, 0.6)' : 'rgba(250, 204, 21, 0.35)'}
            strokeWidth="8"
            strokeLinecap="round"
            strokeDasharray={`${sweetSpotDashLength} ${RING_CIRCUMFERENCE - sweetSpotDashLength}`}
            strokeDashoffset={sweetSpotDashOffset}
            transform="rotate(-90 104 104)"
          />
        )}

        {/* Progress arc */}
        {isWaiting && (
          <circle
            cx="104"
            cy="104"
            r={RING_RADIUS}
            stroke="#3b82f6"
            strokeWidth="5"
            strokeLinecap="round"
            strokeDasharray={RING_CIRCUMFERENCE}
            strokeDashoffset={dashoffset}
            transform="rotate(-90 104 104)"
            /* No CSS transition — arc must track progress exactly for sweet spot alignment */
          />
        )}
      </svg>

      <button
        onClick={handleClick}
        disabled={isInventoryFull}
        className={`
          w-48 h-48 rounded-full text-6xl
          transition-all duration-200 relative
          ${getButtonClass()}
        `}
      >
        <span className="relative inline-block">
          🎣
          {isInventoryFull && (
            <>
              {/* Red diagonal line through the emoji */}
              <span className="absolute inset-0 flex items-center justify-center">
                <span className="w-full h-1 bg-red-500 rotate-45 rounded-full" />
              </span>
              <span className="absolute inset-0 flex items-center justify-center">
                <span className="w-full h-1 bg-red-500 -rotate-45 rounded-full" />
              </span>
            </>
          )}
        </span>
      </button>

      {/* Click result flash */}
      {clickResult === 'perfect' && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <span
            className="text-yellow-300 font-black text-xl"
            style={{
              textShadow: '0 0 10px rgba(250, 204, 21, 0.8), 0 0 20px rgba(250, 204, 21, 0.4)',
              animation: 'perfectFlash 0.6s ease-out forwards',
            }}
          >
            Perfect!
          </span>
        </div>
      )}
      {clickResult === 'good' && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <span
            className="text-blue-300 font-bold text-lg"
            style={{
              textShadow: '0 0 8px rgba(147, 197, 253, 0.7), 0 0 16px rgba(96, 165, 250, 0.3)',
              animation: 'perfectFlash 0.6s ease-out forwards',
            }}
          >
            Good!
          </span>
        </div>
      )}
      {clickResult === 'miss' && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <span
            className="text-red-400/70 font-medium text-base"
            style={{
              animation: 'perfectFlash 0.4s ease-out forwards',
            }}
          >
            Miss!
          </span>
        </div>
      )}

      {/* Timing tutorial overlay — shown once on first-ever fishing cast */}
      {showTimingTutorial && isWaiting && (
        <div className="absolute inset-0 flex items-center justify-center z-40">
          <div className="absolute -top-28 left-1/2 transform -translate-x-1/2 w-56 bg-gray-900/95 border border-yellow-400/50 rounded-xl px-4 py-3 text-center shadow-xl">
            <p className="text-yellow-300 font-bold text-sm mb-1">Timing Bonus!</p>
            <p className="text-gray-200 text-xs mb-2">
              Click when the progress reaches the golden zone for a catch bonus!
            </p>
            <div className="text-xs text-gray-300 space-y-0.5 mb-2">
              <p><span className="text-yellow-300 font-semibold">Perfect</span> = +15% catch chance</p>
              <p><span className="text-blue-300 font-semibold">Good</span> = +7% catch chance</p>
            </div>
            <button
              onClick={(e) => {
                e.stopPropagation();
                dismissTimingTutorial();
              }}
              className="bg-yellow-500 hover:bg-yellow-400 text-gray-900 font-bold text-xs px-4 py-1 rounded-lg transition-colors"
            >
              Got it!
            </button>
            {/* Arrow pointing down to the ring */}
            <div className="absolute -bottom-2 left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-8 border-r-8 border-t-8 border-l-transparent border-r-transparent border-t-gray-900/95" />
          </div>
        </div>
      )}

      {/* Custom tooltip that works on mobile */}
      {showTooltip && !showTimingTutorial && (
        <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-sm rounded-lg max-w-[14rem] text-center pointer-events-none z-50 shadow-lg">
          {tooltipText}
          {/* Arrow pointing down */}
          <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-l-transparent border-r-transparent border-t-gray-900" />
        </div>
      )}

      {/* Keyframe for click result text */}
      <style>{`
        @keyframes perfectFlash {
          0%   { transform: scale(0.5) translateY(0); opacity: 0; }
          30%  { transform: scale(1.2) translateY(-8px); opacity: 1; }
          100% { transform: scale(1.0) translateY(-20px); opacity: 0; }
        }
      `}</style>

      {/* Fly Cast overlay — appears BEFORE fishing starts when fly rod equipped */}
      <FlyCastOverlay visible={showFlyCast} onDismiss={handleFlyCastComplete} />
    </div>
  );
}
